import styled, { css } from 'styled-components'
import Dropzone from 'react-dropzone'
import {
	IconComponent,
	LinkComponent,
	themeColor,
	themeFontFamily,
	ContentBlockComponent,
	IframeComponent,
} from 'LibNeo'
import { PopoverOverlayStyled } from 'LibNeo/components/Basic/Popover/styled'
import { LinkComponentStyled } from 'LibNeo/components/Basic/Link/styled'

export const ButtonZone = styled(Dropzone)`
	border: none;
	display: flex;
	flex-shrink: 0;
	flex-grow: 1;
`

export const ButtonZoneWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-start;
`

type FileWrapperItemProps = {
	fixWidth?: boolean
}
export const FileWrapperItem = styled.div<FileWrapperItemProps>`
	margin-top: 16px;
	width: 100%;
	z-index: 1;

	${({ fixWidth }) =>
		fixWidth &&
		css`
			max-width: 400px;
		`}
`

export const UploadPopoverOverlayStyled = styled(PopoverOverlayStyled)`
	.ant-popover {
		z-index: 1500;
	}
	.ant-popover-arrow {
		display: none;
	}

	.ant-popover-inner {
		margin-top: -26px;
		padding: 0;
		background: none;
		border: none;
		box-shadow: none;
	}
`

export const FileWrapper = styled.div`
	display: flex;
	flex-direction: column;
	padding-left: 4px;
	width: calc(100% - 30px);
`

export const FileNameWrapper = styled.div`
	max-width: calc(100% - 28px);
	white-space: nowrap;
	padding-right: 25px;
	display: flex;
	justify-content: flex-start;
	align-items: center;
`

export const FileInfo = styled.div`
	font-family: ${themeFontFamily('SBSansText')};
	font-size: 12px;
	line-height: 20px;
	text-decoration: none;
	margin-left: 22px;
	margin-top: 2px;
	color: ${themeColor('gray', 5)};
`

export const FileIcon = styled(IconComponent)`
	transform: rotate(180deg);
	margin-right: 6px;
	flex-shrink: 0;
`

export const FileLink = styled(LinkComponent)`
	font-family: ${themeFontFamily('SBSansText')};
	font-size: 12px;
	line-height: 20px;
	text-decoration: none;
`

type DropzoneAreaProps = {
	disabledClick?: boolean
	dragEnter?: boolean
	$width: number | string
	$height: number
}

export const DropzoneArea = styled(Dropzone)<DropzoneAreaProps>`
	display: flex;
	flex-direction: column;
	position: relative;
	justify-content: center;
	min-height: 168px;
	height: ${({ $height }) => `${$height}px`};
	width: ${({ $width }) => ($width === 'auto' ? 'auto' : `${$width}px`)};
	align-items: center;
	background-color: ${themeColor('white')};
	box-sizing: border-box;
	padding: 24px 40px;
	border-radius: 6px;
	cursor: pointer;
	line-height: 20px;
	font-size: 14px;
	color: ${themeColor('gray', 8)};
	font-family: ${themeFontFamily('SBSansText')};

	&:before {
		position: absolute;
		top: 0;
		left: 0;
		content: '';
		width: 100%;
		height: 100%;
		border: 1px dashed ${themeColor('gray', 3)};
		border-radius: 6px;
	}

	&:hover {
		&:before {
			border: 2px dashed ${themeColor('emerald', 5)};
		}
	}

	&:focus {
		&:before {
			border: 2px dashed ${themeColor('emerald', 5)};
		}
	}

	${({ dragEnter }) =>
		dragEnter &&
		css`
			&:before {
				border: 2px dashed ${themeColor('emerald', 5)};
			}
		`}

	${({ disableClick }) =>
		disableClick &&
		css`
			background-color: ${themeColor('gray', 1)};
			pointer-events: none;
			cursor: default;

			&:before {
				border: none;
			}
		`}
`

export const DropzoneTitle = styled.div`
	margin-top: 12px;
	white-space: pre-wrap;
	text-align: center;
`

export const DropzoneSubtitle = styled.div`
	font-size: 12px;
	color: ${themeColor('gray', 5)};
	margin-top: 8px;
	white-space: pre-wrap;
	text-align: center;
`

export const FileError = styled.div`
	font-family: ${themeFontFamily('SBSansText')};
	font-style: normal;
	font-weight: 400;
	font-size: 12px;
	line-height: 20px;
	margin-left: 16px;
	color: ${themeColor('pink', 5)};
	max-width: 400px;
`

export const DropzoneStyled = styled(Dropzone)<{
	disabled?: boolean
	hasError?: boolean
	success?: boolean
}>`
	display: inline-block;

	${({ disabled }) =>
		disabled &&
		css`
			background-color: ${themeColor('gray', 1)};
			border: none;
			pointer-events: none;
			cursor: default;

			&:before {
				border: none;
			}
		`}

	${({ hasError }) =>
		hasError &&
		css`
			background-color: ${themeColor('pink', 1)};

			&:before {
				border: 1px dashed ${themeColor('pink', 5)};
			}
		`}

	${({ success }) =>
		success &&
		css`
			&:before {
				border: none;
			}
		`}
`

export const FilePreview = styled(ContentBlockComponent)`
	width: auto;
	height: auto;
	overflow: hidden;

	img {
		display: inline-block;
		width: 100%;
		height: 100%;
		max-width: 360px;
		max-height: 254px;
		object-fit: contain;
		object-position: center;
	}
`

export const FileItemWrapper = styled.div`
	background-color: ${themeColor('gray', 1)};
	margin-top: 8px;
	padding: 4px;
	border-radius: 6px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	${FilePreview} {
		margin-bottom: 24px;
	}
`

export const FileExt = styled.span`
	color: ${themeColor('emerald', 5)};
	cursor: pointer;
	text-transform: lowercase;
	text-decoration: none;
	flex-shrink: 0;
	line-height: 0;
`

export const FileName = styled.span<{ notView?: boolean }>`
	max-width: calc(100% - 25px);
	white-space: nowrap;
	text-decoration: none;
	line-height: 0;

	${LinkComponentStyled} {
		width: 100%;
		display: inline-block;
		vertical-align: top;
		overflow: hidden;
		text-overflow: ellipsis;
		text-transform: lowercase;
	}

	&:hover {
		cursor: pointer;
	}

	${({ notView }) =>
		notView &&
		css`
			text-decoration: none;
			&:hover {
				cursor: inherit;
			}
		`}
`

export const FileNamePreview = styled.span`
	display: inline-block;
	color: ${themeColor('extraBackground3')};
	font-family: ${themeFontFamily('SBSansText')};
	font-size: 18px;
	font-weight: 600;
	line-height: 24px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 60%;
	vertical-align: bottom;
`

export const FileExtPreview = styled.span`
	margin-right: 8px;
	color: ${themeColor('gray', 8)};
	font-family: ${themeFontFamily('SBSansText')};
	font-size: 18px;
	font-weight: 600;
	line-height: 24px;
`

export const PreviewImage = styled.img`
	max-width: calc(100vw - 160px);
	max-height: calc(100vh - 160px);
	object-position: center;
	object-fit: contain;
`

export const IframeStyled = styled(IframeComponent)`
	max-width: calc(100vw - 80px);
`

export const Icon = styled(IconComponent)`
	cursor: pointer;
`
